/**
 * The Holdings tab with room: four pictures and a Table switch
 * (`docs/mockups/holdings-13f.html` §3b; the numbers are `lib/holdings-expanded.ts`).
 *
 * Shown by `HoldingsView` whenever the panel is wide enough — the detached (⧉)
 * window, or a wide dock — rather than through a route of its own: the data
 * panel already detaches as a whole, and one view that adapts is one view to
 * keep right. The summary box and the holder rows are the compact view's own,
 * handed in, so the two layouts cannot disagree about a number.
 *
 * | picture | answers |
 * |---|---|
 * | Exposure by fund | who is long, who is hedged — each row is that fund's 100% |
 * | Conviction | who cares — $ held against the share of the fund's own book |
 * | Price vs trades | the base quarter as a bridge: price move, then each fund's trade |
 * | Ownership over time | who has been there, and since when |
 */
import { useState, type CSSProperties, type ReactElement, type ReactNode } from "react";
import type { Holding, HoldingsSymbol } from "../api/holdings";
import { conviction, exposure, logScale, ownership, waterfall, type WaterStep } from "../lib/holdings-expanded";
import { fundColor, pct, quarterLabel, shareChange, usd } from "../lib/holdings-view";

interface Props {
  h: HoldingsSymbol;
  summary: ReactNode;
  holders: ReactNode;
}

export function HoldingsExpanded({ h, summary, holders }: Props): ReactElement {
  const [mode, setMode] = useState<"visual" | "table">("visual");
  return (
    <div style={PAGE} data-testid="holdings-expanded">
      <div style={BAR_TOP}>
        <span style={MUTED}>{h.symbol} · latest filed {quarterLabel(h.window.base)} · {h.holders.length} of 15 hold shares</span>
        <span style={SEG_WRAP} role="tablist" aria-label="Holdings layout">
          {(["visual", "table"] as const).map((m) => (
            <button key={m} type="button" role="tab" aria-selected={mode === m} onClick={() => setMode(m)}
                    style={{ ...SEG_BTN, ...(mode === m ? SEG_ON : {}) }}>
              {m === "visual" ? "Visual" : "Table"}
            </button>
          ))}
        </span>
      </div>
      {summary}
      {mode === "visual" ? (
        <div style={GRID}>
          <Card title="Exposure by fund" how="who is long, who is hedged · each row = 100% of that fund's gross, $ on the right"><Exposure h={h} /></Card>
          <Card title="Conviction" how="x = $ held (log) · y = % of that fund's book · size = how much it changed"><Conviction h={h} /></Card>
          <Card title={`${quarterLabel(prevOf(h))} → ${quarterLabel(h.window.base)}: price vs trades`} how="what the stock did to their position, then what they did"><Waterfall steps={waterfall(h)} /></Card>
          <Card title="Ownership over time" how="shade = value against each fund's own peak · ● entered · ✕ exited"><Ownership h={h} /></Card>
          <Card title="Holders by quarter" how="each fund's year across: $ traded and the change in shares, oldest quarter first" wide>{holders}</Card>
        </div>
      ) : (
        <HoldersTable h={h} />
      )}
    </div>
  );
}

function prevOf(h: HoldingsSymbol): string {
  const i = h.quarters.indexOf(h.window.base);
  return h.quarters[i - 1] ?? h.window.base;
}

// ── exposure ───────────────────────────────────────────────────────────────
function Exposure({ h }: { h: HoldingsSymbol }): ReactElement {
  const rows = exposure(h);
  if (!rows.length) return <div style={MUTED}>no positions</div>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0,170px) 1fr 1fr 96px", rowGap: 5, alignItems: "center" }}>
      <span style={HD}>fund</span><span style={{ ...HD, textAlign: "right", paddingRight: 6 }}>◀ puts</span>
      <span style={{ ...HD, paddingLeft: 6 }}>shares + calls ▶</span><span style={{ ...HD, textAlign: "right" }}>$ long</span>
      {rows.map((r) => (
        <span key={r.fund.id} style={{ display: "contents" }}>
          <span style={NM}><i style={{ ...DOT, background: fundColor(r.fund.order) }} />{r.fund.name}</span>
          <span style={{ display: "flex", justifyContent: "flex-end", borderRight: "1px solid var(--qf-text-muted)", height: 16 }}>
            {r.puts > 0 && <span title={`puts ${usd(r.puts)}`} style={{ width: `${100 * r.putsW}%`, ...HATCH("var(--qf-negative)") }} />}
          </span>
          <span style={{ display: "flex", height: 16 }}>
            {r.shares > 0 && <span title={`shares ${usd(r.shares)}`} style={{ width: `${Math.max(1.5, 100 * r.sharesW)}%`, background: statusColor(r.status) }} />}
            {r.calls > 0 && <span title={`calls ${usd(r.calls)}`} style={{ width: `${100 * r.callsW}%`, ...HATCH("var(--qf-positive)") }} />}
          </span>
          <span style={{ ...NUM, textAlign: "right" }}>
            {usd(r.shares + r.calls)}{r.puts > 0 && <><br /><span style={{ ...NEG, fontSize: 11 }}>−{usd(r.puts).replace("−", "")} puts</span></>}
          </span>
        </span>
      ))}
    </div>
  );
}

// ── conviction ─────────────────────────────────────────────────────────────
function Conviction({ h }: { h: HoldingsSymbol }): ReactElement {
  const pts = conviction(h);
  if (!pts.length) return <div style={MUTED}>no stock holders</div>;
  const W = 460, H = 250, P = 42;
  const s = logScale(pts.map((p) => p.value));
  const wmax = Math.max(...pts.map((p) => p.pctBook)) * 1.15 || 0.01;
  const X = (v: number) => P + s.x(v) * (W - P - 14);
  const Y = (w: number) => H - 26 - (w / wmax) * (H - 44);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block" }} role="img" aria-label="Conviction: $ held against share of each fund's book">
      {s.ticks.map((t) => (
        <g key={t}>
          <line x1={X(t)} x2={X(t)} y1={10} y2={H - 26} stroke="var(--qf-border)" />
          <text x={X(t)} y={H - 9} fill="var(--qf-text-muted)" fontSize={11} textAnchor="middle">{usd(t)}</text>
        </g>
      ))}
      {[0, wmax / 2, wmax].map((w) => (
        <text key={w} x={P - 5} y={Y(w) + 4} fill="var(--qf-text-muted)" fontSize={11} textAnchor="end">{pct(w)}</text>
      ))}
      <text x={W - 12} y={20} fill="var(--qf-text-muted)" fontSize={11} textAnchor="end">big and high conviction ↗</text>
      {pts.map((p) => {
        const r = 7 + 10 * p.change;
        const initials = p.fund.manager.split(" ").map((w) => w[0]).join("").slice(0, 2);
        return (
          <g key={p.fund.id}>
            <circle cx={X(p.value)} cy={Y(p.pctBook)} r={r} fill={fundColor(p.fund.order)} fillOpacity={0.85} stroke="var(--qf-background)">
              <title>{`${p.fund.name}: ${usd(p.value)}, ${pct(p.pctBook)} of its book${p.status ? `, ${p.status}` : ""}`}</title>
            </circle>
            <text x={X(p.value)} y={Y(p.pctBook) + 4} fontSize={10} fontWeight={600} textAnchor="middle" fill="#fff" pointerEvents="none">{initials}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ── price vs trades ────────────────────────────────────────────────────────
function Waterfall({ steps }: { steps: WaterStep[] }): ReactElement {
  if (!steps.length) return <div style={MUTED}>needs two filed quarters</div>;
  const top = Math.max(...steps.map((s) => s.hi)) * 1.02 || 1;
  const L = (v: number) => `${(100 * v) / top}%`;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0,170px) 1fr 104px", rowGap: 4, alignItems: "center" }}>
      {steps.map((s) => {
        const bg = s.kind === "total" ? "var(--qf-accent)" : s.kind === "price" ? (s.value >= 0 ? "var(--qf-text-muted)" : "var(--qf-negative)")
          : s.value >= 0 ? "var(--qf-positive)" : "var(--qf-negative)";
        return (
          <span key={s.key} style={{ display: "contents" }}>
            <span style={{ ...NM, fontWeight: s.kind === "total" ? 600 : 400 }}>
              {s.fund && <i style={{ ...DOT, background: fundColor(s.fund.order) }} />}{s.label}
            </span>
            <span style={{ position: "relative", height: 16 }}>
              <span style={{ position: "absolute", top: 0, bottom: 0, left: L(s.lo), width: `max(2px, ${L(s.hi - s.lo)})`, background: bg,
                             opacity: s.kind === "price" ? 0.55 : 1, borderRadius: 2 }} />
            </span>
            <span style={{ ...NUM, textAlign: "right", ...(s.kind === "total" ? {} : s.value >= 0 ? POS : NEG) }}>
              {s.kind === "total" ? usd(s.value) : usd(s.value, true)}
            </span>
          </span>
        );
      })}
    </div>
  );
}

// ── ownership over time ────────────────────────────────────────────────────
function Ownership({ h }: { h: HoldingsSymbol }): ReactElement {
  const rows = ownership(h);
  if (!rows.length) return <div style={MUTED}>no holdings in the year</div>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: `minmax(0,170px) repeat(${h.quarters.length}, 1fr)`, gap: 2, alignItems: "center" }}>
      <span />
      {h.quarters.map((q) => <span key={q} style={{ ...HD, textAlign: "center", ...(q === h.window.qn ? { color: "var(--qf-warn)" } : {}) }}>{quarterLabel(q)}</span>)}
      {rows.map((r) => (
        <span key={r.fund.id} style={{ display: "contents" }}>
          <span style={NM}><i style={{ ...DOT, background: fundColor(r.fund.order) }} />{r.fund.name}</span>
          {r.cells.map((c, i) => (
            <span key={i} title={c.pending ? "not filed yet" : c.value ? usd(c.value) : "none"}
                  style={{ height: 22, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12,
                           ...(c.pending ? HATCH("var(--qf-border)") : c.value ? { background: "var(--qf-accent)", opacity: 0.2 + 0.8 * c.shade } : { border: "1px solid var(--qf-border)" }) }}>
              {c.mark === "entered" ? "●" : c.mark === "exited" ? "✕" : ""}
            </span>
          ))}
        </span>
      ))}
    </div>
  );
}

// ── the Table switch ───────────────────────────────────────────────────────
type SortKey = "fund" | "value" | "pctBook" | "pctOut" | "trade";

function HoldersTable({ h }: { h: HoldingsSymbol }): ReactElement {
  const [sort, setSort] = useState<{ k: SortKey; dir: 1 | -1 }>({ k: "value", dir: -1 });
  const rows: Holding[] = [...h.holders, ...h.exited];
  const key = (x: Holding): number | string => sort.k === "fund" ? x.fundName : (x[sort.k] ?? -Infinity);
  rows.sort((a, b) => (key(a) > key(b) ? 1 : key(a) < key(b) ? -1 : 0) * sort.dir);
  const th = (k: SortKey | null, label: string, right = false) => (
    <th style={{ ...TH, textAlign: right ? "right" : "left", cursor: k ? "pointer" : "default", color: sort.k === k ? "var(--qf-accent)" : TH.color }}
        onClick={k ? () => setSort((s) => ({ k, dir: s.k === k ? (s.dir === 1 ? -1 : 1) : k === "fund" ? 1 : -1 })) : undefined}>
      {label}{sort.k === k ? (sort.dir < 0 ? " ▾" : " ▴") : ""}
    </th>
  );
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>{th("fund", "Fund / manager")}{th("pctBook", "% of fund", true)}{th("value", "$ held", true)}{th("pctOut", `% of ${h.symbol}`, true)}
            {th("trade", `${quarterLabel(h.window.base)} trade`, true)}{th(null, "shares", true)}{th(null, "calls · puts", true)}{th(null, "filed")}</tr>
        </thead>
        <tbody>
          {rows.map((x, i) => (
            <tr key={x.fund} style={i % 2 ? { background: "var(--qf-row-alt)" } : undefined}>
              <td style={TD}><span style={NM}><i style={{ ...DOT, background: fundColor(x.fundOrder) }} />{x.fundName}</span><div style={{ ...MUTED, fontSize: 11 }}>{x.manager}</div></td>
              <td style={{ ...TD, ...NUM, textAlign: "right" }}>{pct(x.pctBook)}</td>
              <td style={{ ...TD, ...NUM, textAlign: "right" }}>{usd(x.value)}</td>
              <td style={{ ...TD, ...NUM, textAlign: "right" }}>{pct(x.pctOut)}</td>
              <td style={{ ...TD, ...NUM, textAlign: "right", ...((x.trade ?? 0) >= 0 ? POS : NEG) }}>{usd(x.trade, true)}</td>
              <td style={{ ...TD, ...NUM, textAlign: "right" }}>{shareChange(x.trade, x.value, x.status)}</td>
              <td style={{ ...TD, ...NUM, textAlign: "right" }}>{x.calls || x.puts ? `${usd(x.calls)} · ${usd(x.puts)}` : "—"}</td>
              <td style={{ ...TD, ...MUTED }}>{x.filed ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── parts and styles (theme tokens only, apart from the fund palette) ──────
function Card({ title, how, wide, children }: { title: string; how: string; wide?: boolean; children: ReactNode }): ReactElement {
  return (
    <section style={{ ...CARD, ...(wide ? { gridColumn: "1 / -1" } : {}) }}>
      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{title}</h3>
      <div style={{ ...MUTED, fontSize: 11.5, marginBottom: 8 }}>{how}</div>
      {children}
    </section>
  );
}

function statusColor(s: Holding["status"]): string {
  return s === "TRIM" || s === "EXIT" ? "var(--qf-negative)" : s === "NEW" || s === "ADD" ? "var(--qf-positive)" : "var(--qf-accent)";
}

const HATCH = (c: string): CSSProperties => ({ background: `repeating-linear-gradient(45deg, ${c}, ${c} 3px, transparent 3px, transparent 6px)` });
const MONO = "ui-monospace, 'IBM Plex Mono', Consolas, monospace";
const PAGE: CSSProperties = { padding: "10px 14px", display: "flex", flexDirection: "column", gap: 12, overflowY: "auto", height: "100%", color: "var(--qf-text)", fontSize: 13 };
const BAR_TOP: CSSProperties = { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "space-between", flexShrink: 0 };
const GRID: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 12 };
const CARD: CSSProperties = { border: "1px solid var(--qf-border)", borderRadius: 6, padding: "10px 12px", minWidth: 0, background: "var(--qf-surface)" };
const MUTED: CSSProperties = { color: "var(--qf-text-muted)" };
const POS: CSSProperties = { color: "var(--qf-positive)" };
const NEG: CSSProperties = { color: "var(--qf-negative)" };
const NUM: CSSProperties = { fontFamily: MONO, fontSize: 12.5, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const HD: CSSProperties = { fontFamily: MONO, fontSize: 11, color: "var(--qf-text-muted)", textTransform: "uppercase", letterSpacing: ".04em" };
const NM: CSSProperties = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", paddingRight: 8 };
const DOT: CSSProperties = { display: "inline-block", width: 9, height: 9, borderRadius: 2, marginRight: 6 };
const SEG_WRAP: CSSProperties = { display: "inline-flex", border: "1px solid var(--qf-border)", borderRadius: 5, overflow: "hidden" };
const SEG_BTN: CSSProperties = { background: "none", border: "none", padding: "4px 12px", cursor: "pointer", color: "var(--qf-text-secondary)", fontSize: 12.5, fontFamily: "inherit" };
const SEG_ON: CSSProperties = { background: "var(--qf-accent)", color: "var(--qf-background)", fontWeight: 600 };
const TH: CSSProperties = { padding: "6px 8px", borderBottom: "1px solid var(--qf-border)", fontFamily: MONO, fontSize: 11, textTransform: "uppercase", color: "var(--qf-text-muted)", fontWeight: 500, whiteSpace: "nowrap", userSelect: "none" };
const TD: CSSProperties = { padding: "5px 8px", borderBottom: "1px solid var(--qf-border)", verticalAlign: "middle" };
